
import Vue from 'vue'
import * as Papa from 'papaparse'
import * as Helpers from '@/services/SchoolStudentHelpers'
import * as Api from '@/api/SchoolSettingsApi'
import { SchoolStudentDto } from '@ViewModels/SchoolStudentDto'
import { ClassRoomDto } from '@ViewModels/ClassRoomDto'
import { DataSet, ODataService } from '@/services'
import { RouteButton, SaveButton } from '@/components/buttons'
import { CogMenu } from '@/components'
import { PageHeader } from '../components'
import {
   PagedDataTable,
   FilterDropdown,
   FilterText,
   FilterToggleListItem,
   FilterMultiselect,
} from '@/components/lists'
import SelectionPanel from '../components/StudentListSelectionPanel.vue'

export default Vue.extend({
   name: 'StudentList',
   props: {
      schoolId: { type: String, default: '' },
      settings: { type: Boolean, default: false}
   },
   data() {
      return {
         dataset: new DataSet(new ODataService('SchoolStudent')) as IDataSet,
         schoolYear: 0,
         schoolYearOptions: [] as NameValuePair[],
         classrooms: [] as ClassRoomDto[],
         loadedInitialClassroomsForTeacher: false,
         columns: [
            { name: 'FirstName', title: 'First Name' },
            { name: 'LastName', title: 'Last Name' },
            { name: 'ClassRoomNames', title: 'Classes', type: 'list', sortable: false },
            { name: 'Gender', title: 'Gender' },
            { name: 'DateOfBirth', title: 'Date of Birth', type: 'date' },
            { name: 'TargetTypes', title: 'Target', type: 'targettypes', sortable: false },
            { name: 'IsEnabled', title: 'Active', type: 'boolean-hide' },
         ] as IDataTableColumn[],
         downloading: false,
         crumbs: [{ text: 'Students' }] as Breadcrumb[],
      }
   },
   computed: {
      activeSchoolId(): string {
         return this.schoolId ? this.schoolId : this.$store.getters.activeSchoolId
      },
      activeSchoolYear(): number {
         return this.schoolYear
      },
      selectedClassrooms(): string[] {
         const allSelectedClassroomIds = this.$store.getters.classroomIdsFilter as string[]
         const visibleClassroomIds = this.classroomOptions.map(cro => cro.value)
         return allSelectedClassroomIds.filter(classroomId => visibleClassroomIds.includes(classroomId))
      },
      classroomOptions(): NameValuePair[] {
         const data = this.classrooms
            .filter((item: ClassRoomDto) => +item.SchoolYear === +this.schoolYear)
            .map(
               (item: ClassRoomDto) =>
                  ({
                     name: item.Name,
                     value: item.Id,
                  } as NameValuePair)
            )
            .sort((a: NameValuePair, b: NameValuePair) => a.name.localeCompare(b.name))
         return data
      },
   },

   created() {
      this.schoolYear = new Date().getFullYear()
   },
   async mounted() {
      await this.loadFilterOptions()
      this.dataset.setPageSize(50)
      this.dataset.sortColumn = 'FirstName'
      this.dataset.query = [{ name: 'schoolId', value: this.activeSchoolId },{name: 'schoolYear', value: this.activeSchoolYear}]
      this.dataset.expand = ['ReferenceValues', 'TargetTypes']
      this.dataset.filters = [
         { name: 'FirstName', comparison: 'substring', value: '' },
         { name: 'IsEnabled', comparison: 'eq', value: 'true' },
         { name: 'ClassRoomIds', comparison: 'any', value: [...this.selectedClassrooms], type: 'guidlist' },
      ]
      await this.reload()
   },
   methods: {
     debug(msg:string) {
       console.log(msg)
     },
     select(row: SchoolStudentDto) {
         const studentId = row.Id as string
         this.$router.push({
            name: 'student-details',
            params: { schoolId: this.activeSchoolId, studentId },
         })
      },

      schoolYearFilterChanged(value: number) {
         if (value) {
            this.debug('change schoolYear filter to '+value)
            this.schoolYear = value
         }
         // store the new query
         console.log('query', this.dataset.query.toString)
         this.dataset.query = [{ name: 'schoolId', value: this.activeSchoolId },{name: 'schoolYear', value: this.activeSchoolYear}]
         this.$store.dispatch('setSchoolYearFilter', value)
      },

      async classroomFilterChanged(value: string[]) {
         this.debug('change classroom filter to '+value.toString())
         await this.$store.dispatch('setClassroomIdsFilter', value)
         this.dataset.updateFilter('ClassRoomIds', value)
         this.dataset.showPage(1)
         await this.reload()
      },

      async filterTextChanged(value: string) {
         this.dataset.updateFilter('FirstName', value.trim())
         this.dataset.showPage(1)
         await this.reload()
      },

      async filterInactiveChanged(showInactive: boolean) {
         if (showInactive === true) {
            this.dataset.updateFilter('IsEnabled', '')
         } else {
            this.dataset.updateFilter('IsEnabled', 'true')
         }
         this.dataset.showPage(1)
         await this.reload()
      },

      async reload() {
        this.debug('reloading dataset')
        await this.dataset.load()
        this.debug('loaded dataset ' + this.dataset.toString())
      },

      async loadFilterOptions() {
         const results = await Api.getClassrooms(this.activeSchoolId)
         this.classrooms = results

         if (this.$store.getters.classroomIdsFilter.length == 0) 
         {
            const activeTeacher = this.$store.getters.activeSchoolTeacherId
            const activeTeacherClasses = this.classrooms
               .filter(cr => cr.SchoolTeacherIds.includes(activeTeacher))
               .map(cr => cr.Id)
               .filter(v => v != null && v != undefined)

            await this.$store.dispatch('setClassroomIdsFilter', activeTeacherClasses)
            this.dataset.updateFilter('ClassRoomIds', activeTeacherClasses as string[])
            this.dataset.showPage(1)
         }
         this.loadedInitialClassroomsForTeacher = true

         // Ensure current year is one of the options
         const schoolYears = await Api.getSchoolYearOptions(this.activeSchoolId)
         const currentYear = new Date().getFullYear()
         if (schoolYears.find(y => y.value === currentYear) === null) {
            schoolYears.push({ name: currentYear.toString(), value: currentYear })
         }
         this.schoolYearOptions = schoolYears
      },

      async downloadCSV() {
         this.downloading = true
         try {
            const ethnicityOptions = await Api.getEthnicityOptions(this.activeSchoolId)
            const items = Helpers.generateCsv(this.dataset.items, ethnicityOptions)
            const csv = Papa.unparse(items)
            Helpers.download('students.csv', csv)
         } finally {
            this.downloading = false
         }
      },
   },
   components: {
      PageHeader,
      PagedDataTable,
      FilterText,
      FilterDropdown,
      FilterMultiselect,
      FilterToggleListItem,
      RouteButton,
      SaveButton,
      SelectionPanel,
      CogMenu,
   },
})
